import { useSyncExternalStore } from 'react';
import type { Provider, ProviderSettings, Settings } from '@/types';

const STORAGE_KEY = 'puppy.settings';
const DEFAULT_SETTINGS: Settings = {
  llm: { activeProvider: 'claude', providers: {} },
  companionName: '',
  userName: '',
  memoryEnabled: true,
};

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULT_SETTINGS, ...parsed, llm: { ...DEFAULT_SETTINGS.llm, ...parsed.llm, providers: parsed.llm?.providers ?? {} } };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

let state: Settings = load();
const listeners = new Set<() => void>();

function commit(next: Settings) {
  state = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // storage blocked: keep the in-memory value for this session
  }
  for (const l of listeners) l();
}

export const settingsStore = {
  get: (): Settings => state,
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  setActiveProvider(provider: Provider) {
    commit({ ...state, llm: { ...state.llm, activeProvider: provider } });
  },
  updateProvider(provider: Provider, patch: Partial<ProviderSettings>) {
    const current = state.llm.providers[provider] ?? { apiKey: '', model: '' };
    commit({ ...state, llm: { ...state.llm, providers: { ...state.llm.providers, [provider]: { ...current, ...patch } } } });
  },
  clearProvider(provider: Provider) {
    const { [provider]: _removed, ...rest } = state.llm.providers;
    commit({ ...state, llm: { ...state.llm, providers: rest } });
  },
  setNames(companionName: string, userName: string) {
    commit({ ...state, companionName: companionName.trim(), userName: userName.trim(), firstMetAt: state.firstMetAt ?? Date.now() });
  },
  setMemoryEnabled(memoryEnabled: boolean) {
    commit({ ...state, memoryEnabled });
  },
  markInvited() {
    commit({ ...state, invitedAt: Date.now() });
  },
  replace(next: Settings) {
    commit(next);
  },
};

export function useSettings(): Settings {
  return useSyncExternalStore(settingsStore.subscribe, settingsStore.get, settingsStore.get);
}

/** The provider that will answer, or null when no key is configured anywhere. */
export function activeLLM(s: Settings = state): { provider: Provider; apiKey: string; model: string; models: string[] } | null {
  const order: Provider[] = [s.llm.activeProvider, 'claude', 'openai', 'gemini'];
  for (const p of order) {
    const cfg = s.llm.providers[p];
    if (cfg?.apiKey && cfg.model) return { provider: p, apiKey: cfg.apiKey, model: cfg.model, models: cfg.models ?? [] };
  }
  return null;
}
